/*
 * Copyright © Manatee. 2026 All Rights Reserved.
 * Proprietary and confidential.
 */

import { useEffect, useState, type CSSProperties } from "react";
import { useSearchParams } from "react-router";
import { Link } from "../components/ui/Link";
import { CalendarClock, ChevronRight, ExternalLink, PhoneOff } from "lucide-react";
import { poolPreview, removeFromWaitlist, type PoolCandidate } from "../api/pool";
import { newIdempotencyKey } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useChrome } from "../contexts/ChromeContext";
import { useToast } from "../contexts/ToastContext";
import { isCareStaff } from "../lib/accounts";
import { BusyLabel } from "../components/ui/BusyLabel";
import { Button } from "../components/ui/Button";
import { GuardedButton } from "../components/ui/GuardedButton";
import { Modal } from "../components/ui/Modal";
import { EmptyState } from "../components/ui/EmptyState";
import { Badge } from "../components/ui/Badge";
import { HealthieLink } from "../components/HealthieLink";
import { ErrorMessage } from "../components/ui/ErrorMessage";
import { Spinner } from "../components/ui/Spinner";
import { StateSelect } from "../components/StateSelect";


/**
 * The agent's candidate pool, one state at a time: exactly what a freed slot in that state would
 * rank, before provider-specific matching.
 */
export function SchedulingPool() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState(searchParams.get("state") ?? "");
  const [pool, setPool] = useState<PoolCandidate[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [removing, setRemoving] = useState<PoolCandidate | null>(null);
  const { hasRole } = useAuth();
  const { showToast } = useToast();
  const canRemove = isCareStaff(hasRole);

  useChrome({}, []);

  function removed(candidate: PoolCandidate) {
    setRemoving(null);
    setPool((current) =>
      current === null ? current : current.filter((entry) => entry.sourceId !== candidate.sourceId),
    );
    showToast("success", `${candidateName(candidate)} is off the waitlist.`);
  }

  useEffect(() => {
    if (state === "") {
      setPool(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    void poolPreview(state).then((result) => {
      if (cancelled) {
        return;
      }
      setLoading(false);
      if (result.ok) {
        setPool(result.value);
      } else {
        setError(result.error.message);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [state]);

  return (
    <div className="space-y-8">
      <header className="enter">
        <h1 className="font-display text-2xl leading-tight text-text-primary sm:text-[1.75rem]">
          Waitlist pool
        </h1>
        <p className="mt-2 text-[15px] leading-relaxed text-text-secondary">
          Every family a freed slot could be offered to, one state at a time. Waitlist and
          provider-transition candidates appear here before any provider-specific matching.
        </p>
      </header>

      <div
        className="enter flex w-full flex-col gap-3 sm:flex-row sm:items-center"
        style={{ "--enter-delay": "80ms" } as CSSProperties}
      >
        <StateSelect className="w-full sm:w-64" value={state} onChange={setState} />
        {pool !== null && pool.length > 0 && (
          <p className="text-sm text-text-tertiary" aria-live="polite">
            {pool.length === 1 ? "1 family waiting" : `${pool.length} families waiting`}
            {loading && <Spinner size="xs" className="ml-2 inline-block align-middle" />}
          </p>
        )}
      </div>

      <ErrorMessage message={error} className="mb-0" />

      {loading && pool === null && (
        <div className="grid place-items-center py-20">
          <Spinner size="lg" />
        </div>
      )}

      {!loading && pool !== null && pool.length === 0 && (
        <EmptyState
          icon={<CalendarClock size={20} />}
          title={`Nobody is waiting in ${state}`}
          description="A completed waitlist or transition note adds a family here."
        />
      )}

      {pool !== null && pool.length > 0 && (
        <ol className="grid gap-4 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
          {pool.map((candidate, index) => (
            <PoolCard
              key={candidate.patientId}
              candidate={candidate}
              index={index}
              canRemove={canRemove}
              onRemove={() => setRemoving(candidate)}
            />
          ))}
        </ol>
      )}

      <RemoveFromWaitlistModal
        candidate={removing}
        onClose={() => setRemoving(null)}
        onRemoved={removed}
      />
    </div>
  );
}

function candidateName(candidate: PoolCandidate): string {
  const name = [candidate.firstName, candidate.lastName]
    .filter((part) => part !== null)
    .join(" ");
  return name === "" ? "A family" : name;
}

function waitingSince(addedAt: string): string {
  return new Date(addedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function PoolCard({
  candidate,
  index,
  canRemove,
  onRemove,
}: {
  candidate: PoolCandidate;
  index: number;
  canRemove: boolean;
  onRemove: () => void;
}) {
  const name = candidateName(candidate);
  const removable = candidate.source === "WAITLIST" && candidate.sourceId !== null;
  return (
    <li
      className="group border border-border bg-bg-elevated shadow-card lift enter relative flex h-full flex-col gap-3 rounded-3xl p-5"
      style={{ "--enter-delay": `${Math.min(index, 8) * 55 + 70}ms` } as CSSProperties}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="truncate font-display text-xl leading-tight text-text-primary">
            {candidate.familyId !== null ? (
              <Link
                to={`/care/families/${candidate.familyId}`}
                aria-label={`Open ${name}'s family`}
                className="after:absolute after:inset-0 after:rounded-3xl"
              >
                {name}
              </Link>
            ) : (
              name
            )}
          </h2>
          <p className="mt-0.5 truncate text-xs text-text-tertiary">
            {candidate.source === "TRANSITION" ? "Provider transition" : "Waitlist"}
            {candidate.acuity !== null && ` · ${candidate.acuity.toLowerCase()} acuity`}
            {candidate.addedAt !== null && ` · waiting since ${waitingSince(candidate.addedAt)}`}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 text-xs text-text-secondary">
        {candidate.payer !== null ? (
          <span className="rounded-full bg-bg-muted px-2 py-0.5">
            {candidate.payer.displayName}
          </span>
        ) : (
          <Badge tone="warning">No active insurance</Badge>
        )}
        {!candidate.reachable && (
          <Badge tone="error">
            <PhoneOff size={10} className="mr-1 inline-block" />
            Unreachable
          </Badge>
        )}
        {candidate.eligibility === "INELIGIBLE" && candidate.eligibilityReason !== null && (
          <Badge tone="error">{candidate.eligibilityReason}</Badge>
        )}
      </div>

      <div className="mt-auto flex items-center justify-between gap-3 border-t border-border/60 pt-3 text-xs">
        {candidate.healthieId !== null ? (
          <HealthieLink
            healthieId={candidate.healthieId}
            className="relative z-10 inline-flex items-center gap-1.5 text-text-tertiary transition-colors hover:text-accent"
          >
            <ExternalLink size={12} />
            Healthie
          </HealthieLink>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-3">
          {removable && (
            <span className="relative z-10">
              <GuardedButton
                locked={!canRemove}
                hint="Only care staff can take a family off the waitlist."
                variant="ghost"
                ariaLabel={`Remove ${name} from the waitlist`}
                onClick={onRemove}
              >
                Remove
              </GuardedButton>
            </span>
          )}
          {candidate.familyId !== null && (
            <span className="inline-flex items-center gap-1 font-medium text-text-tertiary transition-colors group-hover:text-text-primary">
              Open
              <ChevronRight
                size={14}
                className="transition-transform duration-200 group-hover:translate-x-1"
              />
            </span>
          )}
        </div>
      </div>
    </li>
  );
}

function RemoveFromWaitlistModal({
  candidate,
  onClose,
  onRemoved,
}: {
  candidate: PoolCandidate | null;
  onClose: () => void;
  onRemoved: (candidate: PoolCandidate) => void;
}) {
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey);

  useEffect(() => {
    setError(null);
    setIdempotencyKey(newIdempotencyKey());
  }, [candidate]);

  async function remove(target: PoolCandidate, entryId: string) {
    setSaving(true);
    setError(null);
    const result = await removeFromWaitlist(entryId, idempotencyKey);
    setSaving(false);
    if (result.ok) {
      onRemoved(target);
    } else {
      setError(result.error.message);
    }
  }

  return (
    <Modal isOpen={candidate !== null} onClose={onClose} title="Remove from waitlist" size="sm">
      <div className="space-y-4 px-6 py-5">
        <p className="text-sm leading-relaxed text-text-secondary">
          {candidate === null
            ? ""
            : `${candidateName(candidate)} comes off the waitlist and stops getting offers for open slots. To add them back, complete a new waitlist note in Healthie.`}
        </p>
        {error !== null && (
          <p role="alert" className="text-sm font-medium text-error">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={saving}
            onClick={() =>
              candidate !== null &&
              candidate.sourceId !== null &&
              void remove(candidate, candidate.sourceId)
            }
          >
            <BusyLabel busy={saving} busyText="Removing…">
              Remove from waitlist
            </BusyLabel>
          </Button>
        </div>
      </div>
    </Modal>
  );
}
